import { formatDistanceToNow, intlFormat } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Spinner } from '@/components/ui/spinner';
import { CHAIN_ICONS } from '@/lib/constants';
import { TransferStatus } from '@/lib/query-options';
import { cn } from '@/lib/utils';
import { formatD9Address } from '@/lib/utils/d9';
import MingcuteArrowRightLine from '~icons/mingcute/arrow-right-line';

export function TransferringTransfer({
  className,
  userTransferStatus,
}: {
  className?: string;
  userTransferStatus?: TransferStatus;
}) {
  const { t } = useTranslation();
  // Only show when adminApprovalStatus is pending
  if (
    !userTransferStatus ||
    userTransferStatus.adminApprovalStatus !== 'pending'
  ) {
    return null;
  }

  return (
    <Alert variant="default" className={cn(className)}>
      <Spinner className="size-4 text-amber-500" />
      <AlertTitle>{t('transfer.crossChainTransferProcessing')}</AlertTitle>
      <AlertDescription className="space-y-3 mt-2">
        <p className="text-sm text-foreground/80">{t('transfer.transferInProgress')}</p>

        <div className="space-y-2 text-xs">
          <div className="flex items-center gap-2">
            <span className="text-foreground/60 min-w-[60px]">{t('transfer.requestId')}:</span>
            <span className="font-inter text-foreground/80 break-all">
              {userTransferStatus.requestId}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-foreground/60 min-w-[60px]">{t('transfer.transferPath')}:</span>
            <div className="flex items-center gap-1">
              <img
                src={
                  CHAIN_ICONS[
                    userTransferStatus.fromChain as keyof typeof CHAIN_ICONS
                  ]
                }
                alt={userTransferStatus.fromChain}
                className="size-4 ring-1 ring-foreground/10 rounded-full"
              />
              <span className="font-medium">{userTransferStatus.fromChain}</span>
              <MingcuteArrowRightLine className="size-3 text-foreground/40" />
              <img
                src={
                  CHAIN_ICONS[
                    userTransferStatus.toChain as keyof typeof CHAIN_ICONS
                  ]
                }
                alt={userTransferStatus.toChain}
                className="size-4 ring-1 ring-foreground/10 rounded-full"
              />
              <span className="font-medium">{userTransferStatus.toChain}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-foreground/60 min-w-[60px]">{t('transfer.transferAmount')}:</span>
            <span className="font-medium font-pt-mono text-foreground/80">
              {userTransferStatus.amount} <span className="text-foreground/40">USDT</span>
            </span>
          </div>

          <div className="flex items-start gap-2">
            <span className="text-foreground/60 min-w-[60px]">{t('transfer.fromAddress')}:</span>
            <span className="font-inter text-foreground/80 break-all text-xs">
              {userTransferStatus.fromChain === 'D9'
                ? formatD9Address(userTransferStatus.fromAddress)
                : userTransferStatus.fromAddress}
            </span>
          </div>

          <div className="flex items-start gap-2">
            <span className="text-foreground/60 min-w-[60px]">{t('transfer.toAddress')}:</span>
            <span className="font-inter text-foreground/80 break-all text-xs">
              {userTransferStatus.toChain === 'D9'
                ? formatD9Address(userTransferStatus.toAddress)
                : userTransferStatus.toAddress}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-foreground/60 min-w-[60px]">{t('transfer.createdAt')}:</span>
            <span className="text-foreground/80 font-inter">
              {intlFormat(new Date(userTransferStatus.createdAt))}
              <span className="text-foreground/50 ml-1">
                (
                {formatDistanceToNow(new Date(userTransferStatus.createdAt), {
                  addSuffix: true,
                })}
                )
              </span>
            </span>
          </div>
        </div>
      </AlertDescription>
    </Alert>
  );
}


